import { useState } from "react";
import { toast } from "sonner";
import { Send, Mail } from "lucide-react";
import Reveal from "./Reveal";
import { api } from "@/lib/api";

const PARTNER_TYPES = ["Tour Operator", "Hotel / Lodge", "Travel Agency", "DMC / Ground Handler", "Other"];

export default function LeadCapture() {
  const [form, setForm] = useState({ name: "", company: "", email: "", partner_type: PARTNER_TYPES[0], message: "" });
  const [sending, setSending] = useState(false);
  const [newsEmail, setNewsEmail] = useState("");
  const [newsSending, setNewsSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitLead = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", form);
      toast.success("Application received — our partnerships team will reach out shortly.");
      setForm({ name: "", company: "", email: "", partner_type: PARTNER_TYPES[0], message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const submitNewsletter = async (e) => {
    e.preventDefault();
    setNewsSending(true);
    try {
      const { data } = await api.post("/newsletter", { email: newsEmail });
      if (data.status === "already_subscribed") {
        toast.info("You're already on the list — the next edition is on its way.");
      } else {
        toast.success("Welcome aboard — travel intelligence, monthly.");
      }
      setNewsEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setNewsSending(false);
    }
  };

  return (
    <section id="waitlist" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
      <div className="grid gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-7">
          <div className="rounded-2xl border border-goldline bg-panel/70 p-8 backdrop-blur-xl lg:p-12">
            <p className="kicker mb-4">Partner Waitlist</p>
            <h2 className="font-serif text-3xl font-semibold leading-snug tracking-tight sm:text-4xl">
              Take your seat in the <span className="text-gold">network.</span>
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-muted sm:text-base">
              We're onboarding a limited cohort of operators, lodges and agencies. Tell us who
              you are — priority access is granted in order of application.
            </p>

            <form onSubmit={submitLead} className="mt-8 space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <input
                  data-testid="partner-form-name-input"
                  required
                  placeholder="Full name"
                  value={form.name}
                  onChange={set("name")}
                  className="field"
                />
                <input
                  data-testid="partner-form-company-input"
                  required
                  placeholder="Company"
                  value={form.company}
                  onChange={set("company")}
                  className="field"
                />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <input
                  data-testid="partner-form-email-input"
                  required
                  type="email"
                  placeholder="Work email"
                  value={form.email}
                  onChange={set("email")}
                  className="field"
                />
                <select
                  data-testid="partner-form-type-select"
                  value={form.partner_type}
                  onChange={set("partner_type")}
                  className="field"
                >
                  {PARTNER_TYPES.map((t) => (
                    <option key={t} className="bg-panel">{t}</option>
                  ))}
                </select>
              </div>
              <textarea
                data-testid="partner-form-message-input"
                rows={4}
                placeholder="Tell us about your operation (fleet, properties, destinations…)"
                value={form.message}
                onChange={set("message")}
                className="field resize-none"
              />
              <button
                data-testid="partner-form-submit-button"
                type="submit"
                disabled={sending}
                className="btn-gold disabled:opacity-60"
              >
                <Send size={15} /> {sending ? "Submitting…" : "Request priority access"}
              </button>
            </form>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="flex h-full flex-col justify-between rounded-2xl border border-line bg-gradient-to-b from-coal to-ink p-8 lg:p-12">
            <div>
              <p className="kicker mb-4">Newsletter</p>
              <h3 className="font-serif text-3xl font-semibold leading-snug text-cream">
                The Connect <em className="text-gold not-italic">Journal.</em>
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-muted">
                One editorial dispatch a month — new inventory, rate trends, destination
                intelligence and the stories shaping Southern African travel.
              </p>
            </div>
            <form onSubmit={submitNewsletter} className="mt-10">
              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  data-testid="newsletter-email-input"
                  required
                  type="email"
                  placeholder="you@company.com"
                  value={newsEmail}
                  onChange={(e) => setNewsEmail(e.target.value)}
                  className="field flex-1"
                />
                <button
                  data-testid="newsletter-submit-button"
                  type="submit"
                  disabled={newsSending}
                  className="btn-gold justify-center disabled:opacity-60"
                >
                  <Mail size={15} /> {newsSending ? "Joining…" : "Subscribe"}
                </button>
              </div>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                No noise. Unsubscribe anytime.
              </p>
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
